import type { ThreadMessageLike } from '@assistant-ui/react';
import type { ConversationMessage, RunStepResponse, UiEvent } from '../types';
import type { AskUserRef } from '../types/inbox';
import { stepsToIterations, buildContentFromIterations } from './content-builder';

export interface ConvertMessagesResult {
  messages: ThreadMessageLike[];
  /** Run-level duration from the last assistant message (ms), if available. */
  runDurationMs?: number;
}

/**
 * Last assistant ask_user QUESTION turn whose request has no later answer
 * turn in the same conversation. Used on restore to decide whether to
 * resurrect the interactive question card (the `__ask_user` part is
 * synthesized client-side during the live run and is NOT persisted). This is
 * a cheap pre-filter — the caller must still confirm with the inbox item's
 * status (which covers free-text dismissals and cross-tab answers).
 *
 * ANY later user-role turn counts as answered, not just one carrying a
 * matching `kind: 'answer'` ref: a subsequent prompt either carried the
 * answer ids or triggered the backend closeout fallback that cancels every
 * pending ask for the conversation as `user_dismissed`. Either way the ask
 * is supposed to be terminal — so a stale-`pending` inbox item (e.g. a WS
 * closeout that failed silently server-side) must not re-inject an
 * answerable card into a conversation that has already moved past it.
 */
export function findUnansweredAskRef(
  messages: ConversationMessage[],
): { ref: AskUserRef; messageId: string } | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.askUserRef?.kind !== 'question') continue;
    const requestId = msg.askUserRef.askUserRequestId;
    const answered = messages
      .slice(i + 1)
      .some(
        (m) =>
          (m.askUserRef?.askUserRequestId === requestId && m.askUserRef.kind === 'answer') ||
          m.role === 'user',
      );
    return answered ? undefined : { ref: msg.askUserRef, messageId: msg.messageId };
  }
  return undefined;
}

// ─── Old-format backward compatibility ──────────────────────────────────────
//
// Existing conversations may still have toolUse/toolResult blocks embedded in
// message content (the pre-run-level format). We detect this at runtime and
// fall back to the collapse algorithm so chain-of-thought and tool-call parts
// are reconstructed on reload.

interface OldContentBlock {
  text?: string;
  toolUse?: { toolUseId: string; name: string; input: Record<string, unknown> };
  toolResult?: { toolUseId: string; durationMs?: number; content: Array<{ text: string }> };
}

function hasOldFormatBlocks(messages: ConversationMessage[]): boolean {
  return messages.some((msg) =>
    (msg.content as unknown as OldContentBlock[]).some(
      (b) => b.toolUse != null || b.toolResult != null,
    ),
  );
}

/**
 * Old-format converter: collapses consecutive assistant turns (separated by
 * tool-result-only user messages) into a single assistant message with
 * reasoning + tool-call + text parts.
 */
function convertOldFormat(messages: ConversationMessage[]): ConvertMessagesResult {
  const rawMessages = messages as unknown as Array<{
    messageId: string;
    role: 'user' | 'assistant';
    content: OldContentBlock[];
    createdAt: string;
    durationMs?: number;
  }>;

  // Pass 1: build toolUseId → result text + durationMs lookup
  const toolResultMap = new Map<string, string>();
  const toolDurationMap = new Map<string, number>();
  for (const msg of rawMessages) {
    for (const block of msg.content) {
      if (block.toolResult) {
        const text = block.toolResult.content.map((c) => c.text).join('\n');
        toolResultMap.set(block.toolResult.toolUseId, text);
        if (block.toolResult.durationMs != null) {
          toolDurationMap.set(block.toolResult.toolUseId, block.toolResult.durationMs);
        }
      }
    }
  }

  let runDurationMs: number | undefined;
  const result: ThreadMessageLike[] = [];
  let pendingParts: unknown[] = [];
  let pendingId: string | undefined;
  let pendingCreatedAt: Date | undefined;

  function flushPending() {
    if (pendingParts.length === 0) return;
    result.push({
      role: 'assistant',
      content: pendingParts as ThreadMessageLike['content'],
      id: pendingId,
      createdAt: pendingCreatedAt,
      status: { type: 'complete', reason: 'stop' },
    });
    pendingParts = [];
    pendingId = undefined;
    pendingCreatedAt = undefined;
  }

  for (const msg of rawMessages) {
    if (msg.role === 'user') {
      const hasText = msg.content.some((c) => !!c.text);
      if (hasText) {
        flushPending();
        const textParts = msg.content
          .filter((c) => !!c.text)
          .map((c) => ({ type: 'text' as const, text: c.text! }));
        result.push({
          role: 'user',
          content: textParts,
          id: msg.messageId,
          createdAt: new Date(msg.createdAt),
        });
      }
      continue;
    }

    // Assistant message — accumulate into pending run
    if (!pendingId) {
      pendingId = msg.messageId;
      pendingCreatedAt = new Date(msg.createdAt);
    }

    if (msg.durationMs != null) {
      runDurationMs = msg.durationMs;
    }

    const hasToolUse = msg.content.some((c) => !!c.toolUse);

    for (const block of msg.content) {
      if (block.text) {
        if (hasToolUse) {
          pendingParts.push({ type: 'reasoning' as const, text: block.text });
        } else {
          pendingParts.push({ type: 'text' as const, text: block.text });
        }
      }

      if (block.toolUse) {
        if (block.toolUse.name === 'final_answer') {
          const input = block.toolUse.input as { content?: string };
          if (input?.content) {
            pendingParts.push({ type: 'text' as const, text: input.content });
          }
          continue;
        }

        pendingParts.push({
          type: 'tool-call',
          toolCallId: block.toolUse.toolUseId,
          toolName: block.toolUse.name,
          args: block.toolUse.input,
          argsText: JSON.stringify(block.toolUse.input),
          result: toolResultMap.get(block.toolUse.toolUseId),
          isError: false,
          durationMs: toolDurationMap.get(block.toolUse.toolUseId),
        });
      }
    }
  }

  flushPending();
  return { messages: result, runDurationMs };
}

// ─── New-format converter ───────────────────────────────────────────────────

/**
 * New-format converter: each run produces exactly 2 messages (1 user +
 * 1 assistant) with text-only content. Tool call details live on the Run
 * entity, not in conversation messages.
 *
 * Filters out user messages whose `source` is anything other than `'user'`
 * (or absent). These are synthetic prompts the backend persisted for
 * provenance — e.g. AUI action dispatches with `source: 'aui_action'` —
 * and should not appear in the conversation thread.
 */
function convertNewFormat(
  messages: ConversationMessage[],
  lastRunSteps?: RunStepResponse[],
): ConvertMessagesResult {
  const visibleMessages = messages.filter(
    (m) => m.role !== 'user' || !m.source || m.source === 'user',
  );
  const lastAssistantMsg = [...visibleMessages]
    .reverse()
    .find((m) => m.role === 'assistant');

  const result: ThreadMessageLike[] = visibleMessages.map((msg) => {
    const textParts = msg.content.map((c) => ({ type: 'text' as const, text: c.text }));

    const isLastAssistant = msg === lastAssistantMsg;

    let content: unknown[];
    if (isLastAssistant && lastRunSteps?.length) {
      // Rich restoration from steps — use stepsToIterations + buildContentFromIterations.
      // The message's own persisted text is the authoritative final answer;
      // only promote the trace's last narration to "final answer" when the
      // message has no text of its own (or the narration IS that text) —
      // otherwise a run ending in mid-run commentary would render the
      // commentary as the answer and drop the real one.
      const persistedTexts = textParts.filter((p) => p.text);
      const persistedJoined = persistedTexts.map((p) => p.text).join('\n').trim();
      const lastNarration = [...lastRunSteps]
        .reverse()
        .find((s) => s.type === 'narration');
      const narrationIsAnswer =
        !!persistedJoined && (lastNarration?.text ?? '').trim() === persistedJoined;
      const iterations = stepsToIterations(lastRunSteps, {
        promoteLastNarrationToFinal: !persistedJoined || narrationIsAnswer,
      });
      const hasFinal = [...iterations.values()].some((it) => it.hasFinalAnswer);
      content = buildContentFromIterations(iterations, hasFinal) as unknown[];

      // Append the persisted answer when steps produced no text part (either
      // no narrations at all, or narrations kept in the chain-of-thought).
      const hasTextPart = content.some((p) => (p as { type: string }).type === 'text');
      if (!hasTextPart && persistedTexts.length > 0) {
        content.push(...persistedTexts);
      }
    } else if (
      msg.role === 'assistant' && msg.durationMs != null && msg.durationMs > 0
    ) {
      // Inject a synthetic reasoning part so ChainOfThoughtPrimitive.Root
      // renders the "Thought for Xs" button.
      // NOTE: The reasoning text MUST be non-empty — assistant-ui's
      // fromThreadMessageLike filters out reasoning parts with empty text.
      content = [
        { type: 'reasoning' as const, text: '\u200B' },
        ...textParts,
      ];
    } else {
      content = textParts;
    }

    // Ask_user question turns: the AskUserSummary (rendered from askUserRef in
    // agent-assistant-message) is the canonical visualization. Drop the
    // duplicated question prose so reload matches the live render, which never
    // shows a text body for the asking turn. Keep any synthetic reasoning part
    // so the "Thought for Xs" header still renders. Only the `question` kind is
    // filtered — `answer` turns render their text normally.
    if (msg.askUserRef?.kind === 'question') {
      content = (content as Array<{ type: string }>).filter(
        (p) => p.type !== 'text',
      );
    }

    // Build message metadata. Assistant carries run/duration/delegation;
    // `askUserRef` (the ask_user question↔answer link) rides on either role.
    const customMeta: Record<string, unknown> = {
      ...(msg.role === 'assistant' &&
        msg.durationMs != null && { runDurationMs: msg.durationMs }),
      ...(msg.role === 'assistant' && msg.runId && { runId: msg.runId }),
      ...(msg.role === 'assistant' &&
        msg.delegationRef && { delegationRef: msg.delegationRef }),
      ...(msg.askUserRef && { askUserRef: msg.askUserRef }),
      // Platform-injected turns (event_notification etc.) render as a
      // system-event chip instead of a user bubble — see AgentUserMessage.
      ...(msg.role === 'user' && msg.source && msg.source !== 'user' && { source: msg.source }),
    };
    const hasMeta = Object.keys(customMeta).length > 0;

    return {
      role: msg.role,
      content: content as ThreadMessageLike['content'],
      id: msg.messageId,
      createdAt: new Date(msg.createdAt),
      ...(msg.role === 'assistant' && {
        status: { type: 'complete' as const, reason: 'stop' as const },
      }),
      ...(hasMeta && { metadata: { custom: customMeta } }),
    };
  });

  return { messages: result, runDurationMs: lastAssistantMsg?.durationMs };
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Convert API conversation messages to ThreadMessageLike[].
 *
 * Auto-detects old (toolUse/toolResult embedded) vs new (text-only) format
 * and uses the appropriate conversion strategy.
 *
 * Returns the converted messages plus the run-level durationMs from the last
 * assistant message (used to populate the "Thought for Xs" display).
 */
export function convertMessages(
  messages: ConversationMessage[],
  lastRunSteps?: RunStepResponse[],
): ConvertMessagesResult {
  if (hasOldFormatBlocks(messages)) {
    return convertOldFormat(messages);
  }
  return convertNewFormat(messages, lastRunSteps);
}

/** Canvas-restorable event types */
const CANVAS_EVENT_TYPES = ['show_entity', 'show_artifact'] as const;

/**
 * Find the last UI event matching the given types from conversation messages.
 *
 * Checks new-format uiEvents arrays first, then falls back to scanning
 * old-format toolUse blocks for ui_event tool calls.
 */
export function findLastCanvasEvent(
  messages: ConversationMessage[],
  eventTypes: readonly string[] = CANVAS_EVENT_TYPES,
): UiEvent | null {
  // New format: scan uiEvents arrays
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]!;
    if (msg.role !== 'assistant' || !msg.uiEvents) continue;
    for (let j = msg.uiEvents.length - 1; j >= 0; j--) {
      const evt = msg.uiEvents[j]!;
      if (eventTypes.includes(evt.eventType)) {
        return evt;
      }
    }
  }

  // Old format fallback: scan toolUse blocks for ui_event tool calls
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]!;
    if (msg.role !== 'assistant') continue;
    const blocks = msg.content as unknown as OldContentBlock[];
    for (let j = blocks.length - 1; j >= 0; j--) {
      const part = blocks[j]!;
      if (part.toolUse?.name === 'ui_event') {
        const input = part.toolUse.input as { eventType?: string; data?: Record<string, unknown> };
        if (input.eventType && eventTypes.includes(input.eventType) && input.data) {
          return { eventType: input.eventType, data: input.data };
        }
      }
    }
  }

  return null;
}
